import {
  HttpRequestEntity,
  HttpRequestModelType,
} from 'shared/domain/httpRequest/httpRequestModel';
import { isAbortError } from 'serviceWorker/repository/httpRequest/abortRequest';
import { CONFLICT } from 'shared/contants';
import { getErrorCode } from 'shared/utils/errorHandling';
import {
  NON_RETRYABLE_LOCAL_DOCUMENT_UPLOAD_ERROR_CODES,
} from 'shared/utils/errorHandling/constants';
import {
  broadcastCreatedDocumentationError,
  broadcastUpdatedDocumentationError,
  broadcastDocumentationMovedError,
  broadcastDocumentationDeletedError,
} from 'serviceWorker/broadcasts/documentations';
import { broadcastFailUploadedDocument } from 'serviceWorker/broadcasts/documents';
import { hasStatus, isNotFoundOrForbidden } from './helpers';

/**
 * Removes the queued creation of a file (a document, a documentation) the
 * server refused for good: its parent is gone or not accessible, or a limit is
 * reached. Resolves whether it was removed; any other failure is tried again.
 */
export async function removeRefusedFileCreation(
  httpRequests: { remove: (id: number) => Promise<any> },
  request: HttpRequestEntity,
  error: unknown
): Promise<boolean> {
  if (!isNotFoundOrForbidden(error) && !hasStatus(error, CONFLICT)) {
    return false;
  }
  await httpRequests.remove(request._id);
  return true;
}

export async function handleNonRetryableDocumentUploadError(
  httpRequests: { remove: (id: number) => Promise<any> },
  request: HttpRequestEntity,
  error: unknown
): Promise<boolean> {
  const isDocumentUploadRequest =
    request.method === 'POST' &&
    (request.entityType === HttpRequestModelType.document ||
      request.entityType === HttpRequestModelType.drawing);

  if (!isDocumentUploadRequest) {
    return false;
  }

  const errorCode = getErrorCode(error);
  const isNonRetryableDocumentUploadErrorByCode = Boolean(
    errorCode &&
      NON_RETRYABLE_LOCAL_DOCUMENT_UPLOAD_ERROR_CODES.includes(
        errorCode as (typeof NON_RETRYABLE_LOCAL_DOCUMENT_UPLOAD_ERROR_CODES)[number]
      )
  );

  if (!isNonRetryableDocumentUploadErrorByCode) {
    return false;
  }

  await httpRequests.remove(request._id);
  if (
    request.data &&
    'localId' in request.data &&
    typeof request.data.localId === 'number'
  ) {
    // Mark upload as failed for progress UI without emitting generic error toast.
    broadcastFailUploadedDocument(request.data.localId);
  }
  return true;
}

export function broadcastDocumentationError(
  request: HttpRequestEntity,
  error: any
): void {
  if (request.method === 'POST' && !isAbortError(error)) {
    broadcastCreatedDocumentationError(error);
  } else if (
    request.method === 'PUT' &&
    request.entityType !== HttpRequestModelType.documentationMove
  ) {
    broadcastUpdatedDocumentationError(error);
  } else if (
    request.method === 'PUT' &&
    request.entityType === HttpRequestModelType.documentationMove
  ) {
    broadcastDocumentationMovedError(error);
  } else if (request.method === 'DELETE') {
    broadcastDocumentationDeletedError(error);
  }
}
